import React, { useState } from 'react';
import { Alert, Button, Card, Input, Select, Space, Spin, Statistic, Tabs, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { getMonitorApiBaseUrl } from '../../config/env';
import { MonitorChart } from './MonitorChart';
import { CurrentUsers } from './CurrentUsers';
import { ContainerHealthPanel } from './ContainerHealthPanel';
import { useGameServerHeartbeats, useGameServerMetrics, useMonitorCharts, useTimeWindows } from './queries';
import { CHARTS, TimeFilter } from './types';
import './monitor.css';
import { formatDateTime } from '../../utils/format';

const INTERVALS = ['5s', '10s', '30s', '1m', '5m', '15m', '1h'];
const errorMessage = (error: unknown) => error instanceof Error ? error.message : 'Failed to load monitoring data';

const GAME_SERVER_CHART_TITLES: Record<string, string> = {
  'game-server-containers': 'Running containers',
  'game-server-age': 'Container age',
  'game-server-lifetime': 'Completed container lifetime',
  'game-server-tables': 'Tables per container and total',
  'game-server-players': 'Real players per container and total',
  'game-server-playing': 'Playing tables',
  'game-server-humans-only': 'Human-only tables',
  'game-server-with-bots': 'Tables with bots',
  'game-server-one-bot': 'Tables with one bot',
  'game-server-two-bots': 'Tables with two bots',
  'game-server-cpu': 'CPU usage per container',
  'game-server-memory': 'Memory usage per container',
};

const useMonitorFilter = () => {
  const windows = useTimeWindows();
  const [windowID, setWindowID] = useState<string>();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [interval, setInterval] = useState('');
  const [applied, setApplied] = useState<TimeFilter>();
  const [refresh, setRefresh] = useState(0);
  const [validation, setValidation] = useState('');
  const filter = applied || (windows.data ? { window: windows.data.default } : undefined);

  const apply = () => {
    const choice = windowID || windows.data?.default;
    if (!choice) return;
    let next: TimeFilter;
    if (choice === 'custom') {
      const start = new Date(`${from}Z`);
      const end = new Date(`${to}Z`);
      if (!from || !to || !Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || start >= end) {
        setValidation('Choose valid start and end dates, with the start before the end.');
        return;
      }
      next = { from: start.toISOString(), to: end.toISOString() };
    } else next = { window: choice };
    if (interval) next.interval = interval;
    setValidation('');
    setApplied(next);
    setRefresh((value) => value + 1);
  };

  return { windows, windowID, setWindowID, from, setFrom, to, setTo, interval, setInterval, filter, refresh, validation, apply, setRefresh };
};

type MonitorFilterState = ReturnType<typeof useMonitorFilter>;

const MonitorFilters: React.FC<{ state: MonitorFilterState; loading: boolean; title: string }> = ({ state, loading, title }) => {
  const { windows, windowID, setWindowID, from, setFrom, to, setTo, interval, setInterval, filter, validation, apply, setRefresh } = state;
  return <Card size="small" title={title}>
    {windows.isError ? <Alert type="error" showIcon message={errorMessage(windows.error)}
      action={<Button onClick={() => windows.refetch()}>Retry</Button>} /> : <>
      <Space wrap align="end">
        <label className="block">Time window
          <Select aria-label={`${title} time window`} className="block" style={{ width: 190 }} loading={windows.isLoading}
            value={windowID || windows.data?.default} onChange={setWindowID}
            options={[...(windows.data?.windows.map(({ id, label }) => ({ value: id, label })) || []), { value: 'custom', label: 'Custom range' }]} />
        </label>
        {windowID === 'custom' && <>
          <label className="block">From
            <Input type="datetime-local" aria-label={`${title} from`} value={from} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <label className="block">To
            <Input type="datetime-local" aria-label={`${title} to`} value={to} onChange={(event) => setTo(event.target.value)} />
          </label>
        </>}
        <label className="block">Aggregation interval
          <Select aria-label={`${title} aggregation interval`} className="block" style={{ width: 150 }} value={interval} onChange={setInterval}
            options={[{ value: '', label: 'Auto' }, ...INTERVALS.map((value) => ({ value, label: value }))]} />
        </label>
        <Button type="primary" disabled={!windows.data} onClick={apply}>Apply</Button>
        <Button icon={<ReloadOutlined />} disabled={!filter} loading={loading} onClick={() => setRefresh((value) => value + 1)}>Refresh</Button>
      </Space>
      <div className="mt-3"><Typography.Text type="secondary">Dates and chart times use UTC (DD/MM/YYYY HH:mm:ss).</Typography.Text></div>
      {validation && <Alert className="mt-3" type="error" showIcon message={validation} />}
    </>}
  </Card>;
};

const GameServerPanel: React.FC<{ query: ReturnType<typeof useGameServerMetrics> }> = ({ query }) => {
  if (query.isError) return <Alert type="error" showIcon message="Unable to load game-server metrics" description={errorMessage(query.error)} action={<Button onClick={() => query.refetch()}>Retry</Button>} />;
  if (query.isPending || !query.data) return <Card title="Game servers"><div className="flex justify-center items-center h-32"><Spin tip="Loading game-server metrics"><div className="w-20 h-10" /></Spin></div></Card>;
  return <Card title="Game servers" extra={query.isFetching ? <Spin size="small" /> : undefined}>
    <Space direction="vertical" size="middle" className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {query.data.summaries.map((summary) => <Card size="small" key={summary.environment} title={summary.environment}>
          <div className="grid grid-cols-2 gap-3">
            <Statistic title="Running" value={summary.running ?? '-'} />
            <Statistic title="Reporting" value={summary.reporting} />
            <Statistic title="Tables" value={summary.tables ?? '-'} />
            <Statistic title="Players" value={summary.players ?? '-'} />
            <Statistic title="Mean age (s)" value={summary.mean_age_seconds == null ? '-' : Math.round(summary.mean_age_seconds)} />
            <Statistic title="Mean lifetime (s)" value={summary.mean_lifetime_seconds == null ? '-' : Math.round(summary.mean_lifetime_seconds)} />
          </div>
          {summary.partial && <Typography.Text type="warning">Partial: one or more containers have stale or missing samples.</Typography.Text>}
        </Card>)}
      </div>
      <Typography.Text type="secondary">Fresh samples: {query.data.freshness_seconds}s · Values are latest samples per container, refreshed every minute.</Typography.Text>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {query.data.charts.filter(({ chart_id }) => chart_id !== 'game-server-bots-only').map((chart) => <Card key={chart.chart_id} title={GAME_SERVER_CHART_TITLES[chart.chart_id] || chart.chart_id} size="small" className="min-w-0">
          <MonitorChart title={GAME_SERVER_CHART_TITLES[chart.chart_id] || chart.chart_id} chart={chart} />
        </Card>)}
      </div>
    </Space>
  </Card>;
};

const HEARTBEAT_TITLE = 'Heartbeats received per container';

const GameServerHeartbeatPanel: React.FC<{ query: ReturnType<typeof useGameServerHeartbeats> }> = ({ query }) => {
  if (query.isError) {
    return <Alert type="error" showIcon message="Unable to load game-server heartbeats" description={errorMessage(query.error)}
      action={<Button onClick={() => query.refetch()}>Retry</Button>} />;
  }

  return <Card title="Game server heartbeats" size="small" className="min-w-0" extra={query.isFetching && query.data ? <Spin size="small" /> : undefined}>
    <Space direction="vertical" size="small" className="w-full">
      <Typography.Text type="secondary">
        Heartbeat requests the backend logged per interval, one line per container. A dip to 0 means missed heartbeats; a line ends at the container's last heartbeat.
      </Typography.Text>
      {query.data ? <MonitorChart title={HEARTBEAT_TITLE} chart={query.data} />
        : <div className="flex justify-center items-center h-64"><Spin tip="Loading heartbeats"><div className="w-20 h-10" /></Spin></div>}
    </Space>
  </Card>;
};

const SystemMetricsPanel: React.FC = () => {
  const monitorBaseUrl = getMonitorApiBaseUrl();
  const gameServerFilter = useMonitorFilter();
  const backendFilter = useMonitorFilter();
  const gameServerQuery = useGameServerMetrics(gameServerFilter.filter, gameServerFilter.refresh);
  const heartbeatQuery = useGameServerHeartbeats(gameServerFilter.filter, gameServerFilter.refresh);
  const charts = useMonitorCharts(backendFilter.filter, backendFilter.refresh);
  const first = charts[0];
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 'game-server-stats';
    const stored = window.localStorage.getItem('admin.monitor.active-tab');
    return stored === 'backend-resources' ? stored : 'game-server-stats';
  });

  if (!monitorBaseUrl) return <Alert type="info" showIcon message="Monitor API is not configured"
    description="Set this environment's Monitor address in src/config/env.ts and rebuild admin." />;

  return <Space direction="vertical" size="middle" className="w-full">
    <Tabs
      activeKey={activeTab}
      onChange={(key) => {
        setActiveTab(key);
        window.localStorage.setItem('admin.monitor.active-tab', key);
      }}
      items={[
        {
          key: 'game-server-stats',
          label: 'Game Server Stats',
          children: <Space direction="vertical" size="middle" className="w-full">
            <MonitorFilters state={gameServerFilter} loading={gameServerQuery.isFetching || heartbeatQuery.isFetching} title="Game Server Stats filters" />
            <GameServerPanel query={gameServerQuery} />
            <GameServerHeartbeatPanel query={heartbeatQuery} />
          </Space>,
        },
        {
          key: 'backend-resources',
          label: 'Backend Resources',
          children: <Space direction="vertical" size="middle" className="w-full">
            <MonitorFilters state={backendFilter} loading={charts.some((chart) => chart.isFetching)} title="Backend Resources filters" />
            <CurrentUsers />
            <ContainerHealthPanel />
            {backendFilter.filter && (first.isError ? <Alert type="error" showIcon message="Unable to resolve the chart time range"
              description={errorMessage(first.error)} action={<Button onClick={() => first.refetch()}>Retry dashboard</Button>} /> : <>
              {first.data && <Typography.Text type="secondary">
                {formatDateTime(first.data.from)} – {formatDateTime(first.data.to)} · Interval: {first.data.interval}
              </Typography.Text>}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {CHARTS.map(({ id, title }, index) => {
                  const query = charts[index];
                  return <Card key={id} title={title} size="small" className="min-w-0"
                    extra={query.isFetching && query.data ? <Spin size="small" /> : undefined}>
                    {query.isError ? <Alert type="error" showIcon message={errorMessage(query.error)}
                      action={<Button onClick={() => query.refetch()}>Retry</Button>} />
                      : query.data ? <MonitorChart title={title} chart={query.data} />
                      : <div className="flex justify-center items-center h-64"><Spin tip="Loading metrics"><div className="w-20 h-10" /></Spin></div>}
                  </Card>;
                })}
              </div>
            </>)}
          </Space>,
        },
      ]}
    />
  </Space>;
};

export const MonitorPage: React.FC = () => <div className="monitor-dashboard"><SystemMetricsPanel /></div>;
